import { useState } from 'react'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import type { IOrder } from '../../../../Model/index.js'
import type { SequencingPlatformPreview } from '../../../../Generated/SequencingPlatformPreview.ts'
import type { SequencingPlatformKey } from '../../../../Generated/SequencingPlatform.ts'
import { SequencingPlatforms } from '../../../../Generated/SequencingPlatform.ts'
import {
  getPlatformIssueRemedy,
  getSequencingPlatformInlineBadgeStyle,
  getSequencingPlatformLabel,
  getSequencingPlatformOverrideWarning,
  isEarlyAccessSequencingPlatform,
  isProcessingTypeAllowedForPlatform,
  needsSequencingPlatformChoice
} from '../../../../Util/sequencingPlatform.ts'
import { SequencingPlatformEarlyAccessTag } from '../../../Component/SequencingPlatformEarlyAccessTag.tsx'

export interface SubmitSequencingPlatformProps {
  preview: SequencingPlatformPreview
  order: Pick<IOrder, 'processingType'>
  sampleCount: number
  overrideInFlight: boolean
  onOverride: (platform: SequencingPlatformKey | null) => void
  earlyAccessAcknowledged: boolean
  onEarlyAccessAcknowledgedChange: (acknowledged: boolean) => void
}

/** The platform the submit will route this order on, as the backend computes it, and the lab's way to correct it. */
export const SubmitSequencingPlatform = ({
  preview,
  order,
  sampleCount,
  overrideInFlight,
  onOverride,
  earlyAccessAcknowledged,
  onEarlyAccessAcknowledgedChange
}: SubmitSequencingPlatformProps) => {
  const [overriding, setOverriding] = useState(false)
  const [pending, setPending] = useState<SequencingPlatformKey | null>(null)
  const detected = preview.detectedSequencingPlatform ?? null
  const override = preview.sequencingPlatformOverride ?? null
  const effective = preview.effectiveSequencingPlatform ?? null
  const detectedPlatforms = preview.detectedPlatforms ?? []
  const issues = preview.issues ?? []
  const warning = override ? getSequencingPlatformOverrideWarning(detected, override, sampleCount) : null
  const pendingWarning = pending ? getSequencingPlatformOverrideWarning(detected, pending, sampleCount) : null
  const undetected = !detected && detectedPlatforms.length === 0
  const choosing = needsSequencingPlatformChoice(preview)

  const pick = (platform: SequencingPlatformKey | null) => {
    if (platform && undetected) {
      onOverride(platform)
      setOverriding(false)
    } else if (platform === null) {
      setPending(null)
      onOverride(null)
    } else {
      setPending(platform)
    }
  }

  const confirmPending = () => {
    if (pending) {
      onOverride(pending)
    }
    setPending(null)
    setOverriding(false)
  }
  const detectedLabel = detected
    ? SequencingPlatforms[detected].label
    : detectedPlatforms.length > 1
      ? `mixed (${detectedPlatforms.map((key) => SequencingPlatforms[key].label).join(', ')})`
      : 'not detected'

  return (
    <div className="mt-4 text-xxs xs:text-xs md:text-base" data-testid="submit-sequencing-platform">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold">Sequencing platform:</span>
        {effective ? (
          <span
            data-testid="submit-sequencing-platform-badge"
            className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold ${getSequencingPlatformInlineBadgeStyle(effective)}`}
          >
            {getSequencingPlatformLabel(effective)}
            {override ? (undetected ? ' (chosen)' : ' (overridden)') : ''}
            <SequencingPlatformEarlyAccessTag platform={effective} />
          </span>
        ) : (
          <span data-testid="submit-sequencing-platform-badge" className="italic text-gray-500">
            {detectedPlatforms.length > 1 ? 'Mixed' : 'Not detected'}
          </span>
        )}
        {!overriding && !choosing && (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            data-testid="submit-sequencing-platform-override-toggle"
            aria-label="Change sequencing platform"
            title="Change sequencing platform"
            onClick={() => setOverriding(true)}
          >
            <PencilSquareIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {choosing && (
        <div
          className="mt-2 rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900"
          role="alert"
          data-testid="submit-sequencing-platform-choose"
        >
          <p className="font-bold">Choose the platform these reads came from.</p>
          <p className="mt-1">
            None of the files names its sequencer: re-exported reads, from SRA for example, lose the headers we read it from.
            The platform decides how the reads are processed.
          </p>
        </div>
      )}
      {(overriding || choosing) && (
        <select
          data-testid="submit-sequencing-platform-override"
          className="select select-bordered select-sm mt-2 w-full"
          value={pending ?? override ?? ''}
          disabled={overrideInFlight}
          onChange={(e) => pick(e.target.value === '' ? null : (e.target.value as SequencingPlatformKey))}
        >
          {undetected ? (
            <option value="" disabled>
              Choose a platform…
            </option>
          ) : (
            <option value="" disabled={!isProcessingTypeAllowedForPlatform(order.processingType, detected)}>
              Use detected: {detectedLabel}
            </option>
          )}
          {Object.values(SequencingPlatforms).map(({ key, label }) => (
            <option key={key} value={key} disabled={!isProcessingTypeAllowedForPlatform(order.processingType, key)}>
              {undetected ? label : `Override to ${label}`}
            </option>
          ))}
        </select>
      )}
      {isEarlyAccessSequencingPlatform(effective) && (
        <div
          className="mt-2 rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900"
          role="alert"
          data-testid="submit-sequencing-platform-early-access"
        >
          <p className="font-bold">{getSequencingPlatformLabel(effective)} support is early access.</p>
          <p className="mt-1">
            Primer trimming and consensus calling are still being tuned on real datasets, so results can change when we re-run your order
            with improved settings. We recommend uploading reads that are already demultiplexed and primer-trimmed.
          </p>
          <label className="mt-2 flex cursor-pointer items-center gap-2 font-semibold">
            <input
              type="checkbox"
              className="checkbox checkbox-warning checkbox-sm"
              data-testid="submit-sequencing-platform-early-access-ack"
              checked={earlyAccessAcknowledged}
              onChange={(e) => onEarlyAccessAcknowledgedChange(e.target.checked)}
            />
            <span>I understand that {getSequencingPlatformLabel(effective)} processing is early access and results may change.</span>
          </label>
        </div>
      )}
      {pending && (
        <div className="mt-2 rounded border border-warning bg-warning/10 p-3 text-sm" data-testid="submit-sequencing-platform-override-confirm">
          <p className="font-semibold">You are overriding the platform detected from your files.</p>
          <p className="mt-1">
            The order will be processed as {SequencingPlatforms[pending].label}. If the files are not {SequencingPlatforms[pending].label}{' '}
            data, processing can fail or silently produce wrong results. Results of an overridden platform are your responsibility.
          </p>
          {pendingWarning && <p className="mt-1">{pendingWarning}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="btn btn-sm" onClick={() => setPending(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-warning"
              data-testid="submit-sequencing-platform-override-accept"
              onClick={confirmPending}
            >
              Override anyway
            </button>
          </div>
        </div>
      )}
      {override && !undetected && (
        <p className="mt-1 text-xs text-gray-500">
          Detected: {detectedLabel}. The order runs as {SequencingPlatforms[override].label} because of the override.
        </p>
      )}
      {warning && <p className="mt-2 text-sm text-warning">{warning}</p>}
      {issues.map((issue, index) => {
        const remedy = getPlatformIssueRemedy(issue)
        return (
          <div
            key={`${issue.type}-${index}`}
            className="mt-2 rounded border border-red-400 bg-red-50 p-3 text-red-800"
            data-testid="submit-sequencing-platform-issue"
          >
            <div>{'issue' in issue ? issue.issue : issue.type}</div>
            {remedy && <div className="mt-1">{remedy}</div>}
          </div>
        )
      })}
    </div>
  )
}
